// Wrappers de Administración · Empresa (handoff §2, plan 2026-10-30 §2.2,
// T-17p9) sobre las rutas de autogestión de core-api
// (routes/organization-branding.ts + PATCH /organizations/me): los datos
// legales, el logo de la empresa y las marcas con su logo, siempre sobre
// la organización del JWT. Mismas formas que /admin/orgs/:id/*
// (api-types/organization-branding.ts), así el flujo presign → PUT →
// commit de ./logoUpload.ts es el de features/admin/logoUpload.ts con otra
// URL. La lectura (GET /organizations/me) sigue en
// lib/api/endpoints/organizations.ts y useMyOrganization.

import {
  myOrganizationBrandLogoCommitResponseSchema,
  myOrganizationBrandResponseSchema,
  myOrganizationLogoCommitResponseSchema,
  myOrganizationLogoUploadUrlResponseSchema,
  type MyOrganizationBrandCreateRequest,
  type MyOrganizationBrandLogoCommitResponse,
  type MyOrganizationBrandResponse,
  type MyOrganizationBrandUpdateRequest,
  type MyOrganizationLogoCommitRequest,
  type MyOrganizationLogoCommitResponse,
  type MyOrganizationLogoUploadUrlRequest,
  type MyOrganizationLogoUploadUrlResponse,
  type MyOrganizationResponse,
  type MyOrganizationUpdateBody,
} from "@bitacorapro/api-types";
import { request } from "../../../lib/api/client";

/** Lo que PATCH /organizations/me devuelve: la fila sin los campos derivados. */
export type MyOrganizationPatchResponse = Pick<
  MyOrganizationResponse,
  "id" | "name" | "rut" | "recipeSlug"
>;

function brandPath(brandId: string, suffix = ""): string {
  return `/organizations/me/brands/${encodeURIComponent(brandId)}${suffix}`;
}

export const empresaApi = {
  // PATCH /organizations/me — sólo los campos que cambiaron; 400
  // RUT_INVALID cuando el verificador no cuadra.
  patch: (body: MyOrganizationUpdateBody): Promise<MyOrganizationPatchResponse> =>
    request<MyOrganizationPatchResponse>("PATCH", "core", "/organizations/me", { body }),

  // ── Logo de la empresa: presign → PUT directo a R2/MinIO → commit ──────
  requestOrgLogoUploadUrl: async (
    body: MyOrganizationLogoUploadUrlRequest
  ): Promise<MyOrganizationLogoUploadUrlResponse> => {
    const json = await request<unknown>("POST", "core", "/organizations/me/logo/upload-url", {
      body,
    });
    return myOrganizationLogoUploadUrlResponseSchema.parse(json);
  },
  commitOrgLogo: async (
    body: MyOrganizationLogoCommitRequest
  ): Promise<MyOrganizationLogoCommitResponse> => {
    const json = await request<unknown>("POST", "core", "/organizations/me/logo/commit", {
      body,
    });
    return myOrganizationLogoCommitResponseSchema.parse(json);
  },
  // DELETE /organizations/me/logo — 204; suelta la referencia, el objeto
  // queda para las actas firmadas que lo citan.
  removeOrgLogo: (): Promise<void> => request<void>("DELETE", "core", "/organizations/me/logo"),

  // ── Marcas ─────────────────────────────────────────────────────────────
  // POST /organizations/me/brands — 201 {brand}; 409 BRAND_NAME_TAKEN si
  // ya hay una con ese nombre (sin distinguir mayúsculas).
  createBrand: async (
    body: MyOrganizationBrandCreateRequest
  ): Promise<MyOrganizationBrandResponse> => {
    const json = await request<unknown>("POST", "core", "/organizations/me/brands", { body });
    return myOrganizationBrandResponseSchema.parse(json);
  },
  // PATCH /organizations/me/brands/:brandId — hoy sólo {name}.
  renameBrand: async (
    brandId: string,
    body: MyOrganizationBrandUpdateRequest
  ): Promise<MyOrganizationBrandResponse> => {
    const json = await request<unknown>("PATCH", "core", brandPath(brandId), { body });
    return myOrganizationBrandResponseSchema.parse(json);
  },
  requestBrandLogoUploadUrl: async (
    brandId: string,
    body: MyOrganizationLogoUploadUrlRequest
  ): Promise<MyOrganizationLogoUploadUrlResponse> => {
    const json = await request<unknown>("POST", "core", brandPath(brandId, "/logo/upload-url"), {
      body,
    });
    return myOrganizationLogoUploadUrlResponseSchema.parse(json);
  },
  commitBrandLogo: async (
    brandId: string,
    body: MyOrganizationLogoCommitRequest
  ): Promise<MyOrganizationBrandLogoCommitResponse> => {
    const json = await request<unknown>("POST", "core", brandPath(brandId, "/logo/commit"), {
      body,
    });
    return myOrganizationBrandLogoCommitResponseSchema.parse(json);
  },
};
